import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import ProfileMenu from '../profile/ProfileMenu';
import { useAuth } from '../../contexts/AuthContext';
import styles from './Header.module.scss';

interface NavItemWithSubmenu {
  name: string;
  href?: string;
  submenu?: { name: string; href: string }[];
}

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [publikasiDropdownOpen, setPublikasiDropdownOpen] = useState(false);
  const [publikasiMobileOpen, setPublikasiMobileOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigation: NavItemWithSubmenu[] = [
    // { name: 'Beranda', href: '/' },
    { name: 'Tentang', href: '/about' },
    { name: 'Program', href: '/programs' },
    { name: 'Berita', href: '/news' },
    // { name: 'Kalkulator Zakat', href: '/zakat-calculator' },
    {
      name: 'Publikasi',
      submenu: [
        { name: 'Dashboard Transparansi', href: '/dashboard' },
        { name: 'Laporan Keuangan', href: '/financial-report' },
        { name: 'Laporan Tahunan', href: '/annual-report' },
      ],
    }
  ];

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen((s) => !s);
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setPublikasiMobileOpen(false);
  };

  // Close when route changes
  useEffect(() => {
    closeMobileMenu();
    setPublikasiDropdownOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        closeMobileMenu();
      }

      // Close desktop dropdown when clicking outside
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        publikasiDropdownOpen
      ) {
        setPublikasiDropdownOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    if (publikasiDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen, publikasiDropdownOpen]);

  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  return (
    <header className={styles.header} role="banner">
      <div className={styles.container}>
        <div className={styles.headerContent}>
          {/* Logo */}
          <Link
            to="/"
            className={styles.logo}
            onClick={closeMobileMenu}
            aria-label="Lazismu UMMI - Beranda"
          >
            <img
              src="/logo-lazismu.png"
              alt="Lazismu"
              className={styles.logoImage}
              width="228"
              height="158"
            />
            {/* <span className={styles.logoText}>Lazismu UMMI</span> */}
          </Link>

          {/* Desktop Navigation */}
          <nav className={styles.desktopNav} role="navigation" aria-label="Navigasi utama">
            {navigation.map((item) => {
              if (item.submenu) {
                const isSubmenuActive = item.submenu.some((sub) => isActive(sub.href));
                return (
                  <div key={item.name} className={styles.dropdownContainer} ref={dropdownRef}>
                    <button
                      className={`${styles.navLink} ${styles.dropdownToggle} ${isSubmenuActive ? styles.active : ''
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPublikasiDropdownOpen(!publikasiDropdownOpen);
                      }}
                      aria-expanded={publikasiDropdownOpen}
                      aria-haspopup="true"
                    >
                      {item.name}
                      <ChevronDownIcon
                        className={`${styles.chevron} ${publikasiDropdownOpen ? styles.chevronOpen : ''}`}
                      />
                    </button>
                    {publikasiDropdownOpen && (
                      <div className={styles.dropdownMenu}>
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className={`${styles.dropdownItem} ${isActive(subItem.href) ? styles.active : ''
                              }`}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={item.name}
                  to={item.href!}
                  className={`${styles.navLink} ${isActive(item.href!) ? styles.active : ''}`}
                  aria-current={isActive(item.href!) ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* CTA + Profile (Desktop) */}
          <div className={styles.ctaSection}>
            <ProfileMenu />
          </div>

          {/* Mobile Menu Button */}
          <button
            ref={buttonRef}
            type="button"
            className={styles.mobileMenuButton}
            onClick={toggleMobileMenu}
            onKeyDown={(e) => handleKeyDown(e, toggleMobileMenu)}
            aria-label={isMobileMenuOpen ? 'Tutup menu mobile' : 'Buka menu mobile'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className={styles.icon} />
            ) : (
              <Bars3Icon className={styles.icon} />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className={styles.mobileNavContainer}>
            <nav
              ref={menuRef}
              id="mobile-menu"
              className={styles.mobileNav}
              role="navigation"
              aria-label="Navigasi mobile"
            >
              <ul className={styles.mobileNavList}>
                {navigation.map((item) => {
                  if (item.submenu) {
                    const isSubmenuActive = item.submenu.some((sub) => isActive(sub.href));
                    return (
                      <li key={item.name} className={styles.mobileDropdownItem}>
                        <button
                          className={`${styles.mobileNavLink} ${styles.mobileDropdownToggle} ${isSubmenuActive ? styles.active : ''
                            }`}
                          onClick={() => setPublikasiMobileOpen(!publikasiMobileOpen)}
                          aria-expanded={publikasiMobileOpen}
                        >
                          {item.name}
                          <ChevronDownIcon
                            className={`${styles.chevronMobile} ${publikasiMobileOpen ? styles.chevronOpen : ''
                              }`}
                          />
                        </button>
                        {publikasiMobileOpen && (
                          <ul className={styles.mobileSubmenu}>
                            {item.submenu.map((subItem) => (
                              <li key={subItem.name}>
                                <Link
                                  to={subItem.href}
                                  className={`${styles.mobileSubmenuLink} ${isActive(subItem.href) ? styles.active : ''
                                    }`}
                                  onClick={closeMobileMenu}
                                >
                                  {subItem.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  }
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href!}
                        className={`${styles.mobileNavLink} ${isActive(item.href!) ? styles.active : ''
                          }`}
                        onClick={closeMobileMenu}
                        aria-current={isActive(item.href!) ? 'page' : undefined}
                      >
                        {item.name}
                      </Link>
                    </li>
                  );
                })}

                <li className={styles.mobileProfileItem}>
                  <ProfileMenu />
                </li>
                <li className={styles.mobileCta}>
                  <Button
                    variant="primary"
                    size="md"
                    className="w-full"
                    onClick={() => {
                      closeMobileMenu();
                      window.location.href = '/programs';
                    }}
                  >
                    Donasi Sekarang
                  </Button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
